import { AsyncPipe } from '@angular/common'
import { Component, HostBinding, inject, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { MatDivider } from '@angular/material/divider'
import { MatIconModule } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { MatTooltipModule } from '@angular/material/tooltip'
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { AvatarComponent } from '../avatar/avatar.component'
import { OngoingMeetingCardComponent } from '../ongoing-meeting-card/ongoing-meeting-card.component'
import { MatDialog } from '@angular/material/dialog'
import { CallsService } from '../calls.service'
import { DemoProfilesService } from '../demo-profiles.service'
import { UserWithOrganization } from '../users.service'
import { filter, Subscription } from 'rxjs'
import { UserNamePipe } from '../user-name.pipe'

@Component({
  selector: 'app-side-menu',
  standalone: true,
  imports: [
    MatDivider,
    AsyncPipe,
    MatMenuModule,
    MatIconModule,
    MatTooltipModule,
    TranslatePipe,
    TranslateDirective,
    RouterLink,
    RouterLinkActive,
    AsyncPipe,
    AvatarComponent,
    MatDivider,
    OngoingMeetingCardComponent,
    UserNamePipe,
  ],
  templateUrl: './side-menu.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './side-menu.component.scss',
})
export class SideMenuComponent implements OnInit, OnDestroy {
  @Input()
  user!: UserWithOrganization

  @HostBinding('class.expanded')
  isPermanentlyExpanded!: boolean

  @HostBinding('class.float')
  get isFloating(): boolean {
    return this.isHovered && !this.isPermanentlyExpanded
  }

  callsService = inject(CallsService)
  dialog = inject(MatDialog)
  translateService = inject(TranslateService)
  demoProfilesService = inject(DemoProfilesService)
  router = inject(Router)

  ongoingCalls$ = this.callsService.getOngoingCalls()
  demoProfiles$ = this.demoProfilesService.getAll()
  activeDemoProfile$ = this.demoProfilesService.activeDemoProfileId$

  isHovered = false
  isMenuOpen = false
  routerSubscription?: Subscription

  ngOnInit() {
    window.addEventListener('storage', this.handleStorageEvent)
    this.setIsPermanentlyExpanded()
    this.routerSubscription = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.setIsPermanentlyExpanded()
    })
  }

  setIsPermanentlyExpanded() {
    if (this.router.url.includes('/settings')) {
      this.isPermanentlyExpanded = true
    } else {
      this.isPermanentlyExpanded = localStorage.getItem('isSideMenuPermanentlyExpanded') === 'true'
    }
  }

  ngOnDestroy() {
    window.removeEventListener('storage', this.handleStorageEvent)
    this.routerSubscription?.unsubscribe()
  }

  handleStorageEvent = (event: StorageEvent) => {
    if (event.storageArea === localStorage && event.key === 'isSideMenuPermanentlyExpanded') {
      this.isPermanentlyExpanded = event.newValue === 'true'
    }
  }

  get isExpanded() {
    return this.isHovered || this.isPermanentlyExpanded
  }

  get displayBackdrop() {
    return !this.isPermanentlyExpanded && this.isHovered
  }

  toggleExpansion() {
    this.isPermanentlyExpanded = !this.isPermanentlyExpanded
    localStorage.setItem('isSideMenuPermanentlyExpanded', this.isPermanentlyExpanded.toString())
  }

  get isSettingsExpanded() {
    return this.router.url.includes('/app/settings') && this.isExpanded
  }

  get displayAdminSection() {
    return this.user.role === 'SUPPORT' || this.user.role === 'ADMIN'
  }

  // The page itself is open to admins; only the shortcut to it is held back while it is being trialled.
  get displaySlaDashboardNavigationItem() {
    return this.user.role === 'SUPPORT'
  }

  get isAdminExpanded() {
    return this.router.url.includes('/app/admin') && this.isExpanded
  }

  get isCallsExpanded() {
    return this.router.url.includes('/app/meetings') && this.isExpanded
  }

  signOut(): void {
    window.location.href = '/api/oauth-login/logout-url'
  }

  disableHover() {
    if (this.isMenuOpen) return
    this.isHovered = false
  }

  onMenuClosed() {
    this.isMenuOpen = false
    this.disableHover()
  }

  get displayAddCallButton(): boolean {
    return this.user.organization.resolvedSettings.allowManualCallCreation
  }

  get displayImportCallButton(): boolean {
    return this.user.organization.resolvedSettings.allowCallUpload
  }
}
